import { Divider } from '@mybridge/ui/divider';
import { Stack, HStack } from '@mybridge/ui/layout';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@mybridge/ui/modal';
import { Text } from '@mybridge/ui/text';
import { SlideFade } from '@mybridge/ui/transitions';
import { triggerFetchPosts } from 'packages/mybridgev4/store/actions/post.actions';
import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PostFormBody } from 'v4/components/post-form/body';
import {
  PostFormContext,
  usePostFormContext,
} from 'v4/components/post-form/context';
import { PostFormFooter } from 'v4/components/post-form/footer';
import { PostFormHeader } from 'v4/components/post-form/header';
import { PostBoxContext, usePostBoxContext } from 'v4/components/post/context';
import { PostVisibilityMenu } from 'v4/components/post-form/post-visibility-menu';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useDirection } from '@mybridge/ui/custom-hooks/useDirection';
import { Box } from '@chakra-ui/react';
import { PostFeelingAndTagsText } from 'v4/components/post-form/feeling-tag-text';

export const PostBoxEditModal = ({ ...props }) => {
  const dispatch = useDispatch();
  const { t } = useTranslator();
  const direction = useDirection();
  const { post, refetch, editPostModal } = useContext(PostBoxContext);
  const repost = post?.repost;
  // build a PostBoxContext value for the quoted post
  const repostBoxContext = usePostBoxContext({ post: repost });

  const refetchAll = () => {
    refetch?.();
    editPostModal?.onClose?.();
  };

  const [openPostModal, setOpenPostModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(0); // Add editing index state

  const postFormContext = usePostFormContext({
    post,
    refetchAll,
  });

  const {
    gifs,
    images,
    resetForm,
    videos,
    mode,
    companyId,
    currentPostId,
    visibility,
    formContainerRef,
    expanded,
    feeling: feelings,
    tagged,
    setMultiPost,
    multiPost,
    text,
  } = postFormContext ?? {};
  
  const { companyPageDetail } = useSelector((state) => state.companyPage);
  const { feelingsAndActivitiesArr } = useSelector((s) => s.globalData) ?? {};
  const feeling = feelingsAndActivitiesArr?.find?.((v) => v.id == feelings?.id);

  // Add editing index handler
  const editingIndexHandler = (index) => {
    setEditingIndex(index);
  };

  useEffect(() => {
    if (post) {
      const array = [{ ...post, mode: 'edit' }];
      if (post.child_posts && post.child_posts.length) {
        post.child_posts.forEach((post) => {
          array.push({ ...post, mode: 'edit' });
        });
      }
      setMultiPost(array);
    }
  }, [post, setMultiPost]);

  // Update AddHeaderField function
  const AddHeaderField = () => {
    // Save current editing post data before adding new one
    if (editingIndex !== null && multiPost[editingIndex]) {
      const updatedMultiPost = multiPost.map((item, i) => {
        if (i === editingIndex) {
          return {
            ...item,
            content: text,
            media: images.length ? images : videos.length ? videos : item.media,
            gifs: gifs.length ? gifs : item.gifs,
            gif: gifs.length ? gifs[0]?.images?.original?.url : item.gif,
            tagged: tagged,
            feelings: feelings,
            visibility: visibility
          };
        }
        return item;
      });
      setMultiPost(updatedMultiPost);
    }

    const newPost = {
      text: '',
      content: '',
      gifs: [],
      gif: null,
      images: [],
      videos: [],
      tagged: [],
      feelings: null,
      mode: 'edit',
      companyId,
      id: null, // New post doesn't have ID
      visibility: visibility || 0,
      media: []
    };

    const updatedMultiPost = [...multiPost, newPost];
    setMultiPost(updatedMultiPost);
    
    resetForm(false, true);
    
    // Set focus to the newly added post
    const newIndex = updatedMultiPost.length - 1;
    setEditingIndex(newIndex);
  };

  return (
    <PostFormContext.Provider value={{ ...(postFormContext ?? {}) }}>
      <Modal size="2xl" {...props}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text>{t('postActionMenu.editPost') || "Edit Post"}</Text>
            <ModalCloseButton {...(direction === 'rtl') ? {left: '0.75rem', right: 'auto'} : {left: 'auto', right: '0.75rem'}} />
          </ModalHeader>
          <ModalBody>
            <Stack
              ref={formContainerRef}
              minH={expanded ? '150px' : '40px'}
              transition="all 0.2s"
            >
              <Stack w="100%">
                <PostFormHeader
                  multiPost={multiPost}
                  editingIndex={editingIndex}
                  editingIndexHandler={editingIndexHandler}
                  isPostModalOpen={openPostModal}
                  repost={repost}
                  repostBoxContext={repostBoxContext}
                />
                <SlideFade in={expanded}>
                  {expanded ? (
                    <PostFormBody
                      editingIndex={editingIndex}
                      w="100%"
                      overflow={expanded ? '' : 'hidden'}
                    />
                  ) : (
                    ''
                  )}
                </SlideFade>
              </Stack>
              {expanded ? <Divider /> : ''}
              <Box flexWrap="wrap" mb={3}>
                <PostFeelingAndTagsText
                  fromCreate={true}
                  companyDetails={companyPageDetail}
                  tagged={tagged}
                  feeling={feeling}
                />
              </Box>
              <HStack mb={5}>
                <PostVisibilityMenu
                  handleClose={() => setOpenPostModal(false)}
                  addHeaderField={AddHeaderField}
                  editingIndexHandler={editingIndexHandler}
                  multiPost={multiPost}
                  editingIndex={editingIndex}
                />
              </HStack>
              {expanded ? (
                <SlideFade in={expanded}>
                  <Stack>
                    <PostFormFooter />
                  </Stack>
                </SlideFade>
              ) : (
                ''
              )}
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </PostFormContext.Provider>
  );
};
